/**
 * WeatherWidget - Componente per visualizzare meteo nei punti vendita
 * Versione aggiornata con CSS vanilla
 */
import React, { useState, useEffect } from 'react';
import {
  Sun, Cloud, CloudSun, CloudRain, CloudSnow, CloudLightning,
  CloudDrizzle, CloudFog, Droplets, AlertTriangle
} from 'lucide-react';
import { PrevistiMeteo, MeteoCorrente } from '../types';
import apiService from '../services/api';
import './WeatherWidget.css';

export interface WeatherWidgetProps {
  puntoVenditaId: number;
  compact?: boolean;
}

const WeatherWidget: React.FC<WeatherWidgetProps> = ({ 
  puntoVenditaId, 
  compact = true 
}) => {
  const [meteoCorrente, setMeteoCorrente] = useState<MeteoCorrente | null>(null);
  const [previsioni, setPrevisioni] = useState<PrevistiMeteo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    loadWeatherData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch solo al cambio del punto vendita; loadWeatherData è ricreata ad ogni render
  }, [puntoVenditaId]);

  const loadWeatherData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Carica meteo corrente
      const currentWeather = await apiService.getCurrentWeather(puntoVenditaId);
      setMeteoCorrente(currentWeather);

      // Carica previsioni
      const forecast = await apiService.getWeatherForecast(puntoVenditaId);
      setPrevisioni(forecast.slice(0, 7)); // Solo 7 giorni

    } catch (err: any) {
      console.error('Errore caricamento meteo:', err);
      
      // Gestione errori specifici
      if (err?.response?.status === 401) {
        setError('Accesso non autorizzato');
      } else if (err?.response?.status === 404) {
        setError('Punto vendita non trovato');
      } else if (err?.response?.status === 400) {
        setError('Coordinate non configurate per questo punto vendita');
      } else if (err?.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Meteo non disponibile');
      }
    } finally {
      setLoading(false);
    }
  };

  const getWeatherIcon = (condizione: string, size: number = 20): React.ReactElement => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      'clear': Sun,
      'clouds': Cloud,
      'rain': CloudRain,
      'drizzle': CloudDrizzle,
      'thunderstorm': CloudLightning,
      'snow': CloudSnow,
      'mist': CloudFog,
      'fog': CloudFog,
      'haze': CloudFog
    };

    const condition = condizione.toLowerCase();
    let IconComponent: React.ComponentType<any> = CloudSun; // Default
    for (const [key, icon] of Object.entries(icons)) {
      if (condition.includes(key)) {
        IconComponent = icon;
        break;
      }
    }
    return <IconComponent size={size} style={{ flexShrink: 0 }} />;
  };

  const formatTemperature = (temp: number): string => {
    return `${Math.round(temp)}°C`;
  };

  const formatDate = (date: string): string => {
    const d = new Date(date);
    return d.toLocaleDateString('it-IT', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  if (loading) {
    return (
      <div className="weather-widget loading">
        <div className="weather-loading">
          <span>Caricamento meteo...</span>
        </div>
      </div>
    );
  }

  if (error || !meteoCorrente) {
    return (
      <div className="weather-widget error">
        <div className="weather-error">
          <AlertTriangle size={16} />
          <span>Meteo non disponibile</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`weather-widget ${compact ? 'compact' : 'full'}`}>
      {/* Meteo Corrente */}
      <div className="current-weather">
        <div className="current-main">
          <span className="weather-icon">
            {getWeatherIcon(meteoCorrente.condizione, 28)}
          </span>
          <div className="current-info">
            <div className="current-temp">
              {formatTemperature(meteoCorrente.tempMedia)}
            </div>
            <div className="current-condition">
              {meteoCorrente.condizione}
            </div>
          </div>
        </div>

        {!compact && (
          <div className="current-details">
            <div className="detail-item">
              <span className="detail-label">Min/Max:</span>
              <span className="detail-value">
                {formatTemperature(meteoCorrente.tempMin)}/{formatTemperature(meteoCorrente.tempMax)}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Umidità:</span>
              <span className="detail-value">{meteoCorrente.umidita}%</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Vento:</span>
              <span className="detail-value">{meteoCorrente.ventoVelocita} km/h</span>
            </div>
          </div>
        )}
      </div>

      {/* Toggle per modalità compatta */}
      {compact && (
        <button 
          className="expand-toggle"
          onClick={() => setExpanded(!expanded)}
          title={expanded ? "Riduci previsioni" : "Espandi previsioni"}
        >
          {expanded ? '▲' : '▼'}
        </button>
      )}

      {/* Previsioni 7 giorni */}
      {(expanded || !compact) && previsioni.length > 0 && (
        <div className="forecast">
          <div className="forecast-header">
            <span>Previsioni 7 giorni</span>
          </div>
          <div className="forecast-list">
            {previsioni.map((previsione, index) => (
              <div key={index} className="forecast-item">
                <div className="forecast-date">
                  {index === 0 ? 'Oggi' : formatDate(previsione.data)}
                </div>
                <div className="forecast-icon">
                  {getWeatherIcon(previsione.condizione, 18)}
                </div>
                <div className="forecast-temps">
                  <span className="temp-max">
                    {formatTemperature(previsione.tempMax)}
                  </span>
                  <span className="temp-min">
                    {formatTemperature(previsione.tempMin)}
                  </span>
                </div>
                {previsione.precipitazioni && previsione.precipitazioni > 0 && (
                  <div className="forecast-rain">
                    <span><Droplets size={12} style={{ verticalAlign: 'text-bottom', marginRight: 2 }} />{previsione.precipitazioni}mm</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ultimo aggiornamento */}
      <div className="weather-footer">
        <span className="last-update">
          Aggiornato: {new Date().toLocaleTimeString('it-IT', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      </div>
    </div>
  );
};

export default WeatherWidget;
